import {
  BookOpen,
  CheckCircle2,
  FileText,
  HelpCircle,
  Layers,
  Network,
  Podcast,
  Presentation,
  Sparkles,
  Video,
} from "lucide-react";
import type { StudyMode } from "@/components/content/StudyModeCard";

/**
 * Single source of truth for study modes. Used by the picker page and the
 * dashboard "what can I do" section. Routes are relative to a chapter root.
 */
export const STUDY_MODES: StudyMode[] = [
  {
    id: "ai-tutor",
    label: "AI Tutor",
    description: "Ask questions in plain English about this chapter.",
    icon: Sparkles,
    iconColor: "text-violet-600 dark:text-violet-400",
    iconBg: "bg-violet-500/10",
    alwaysAccessible: true,
  },
  {
    id: "podcast",
    label: "Audio lesson",
    description: "Listen on the go - English, Hindi or Kannada where available.",
    icon: Podcast,
    iconColor: "text-rose-600 dark:text-rose-400",
    iconBg: "bg-rose-500/10",
  },
  {
    id: "video",
    label: "Video overview",
    description: "Watch a guided walkthrough of the chapter.",
    icon: Video,
    iconColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-500/10",
  },
  {
    id: "textbook",
    label: "Smart textbook",
    description: "Searchable, selectable digital textbook.",
    icon: BookOpen,
    iconColor: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-500/10",
  },
  {
    id: "flashcards",
    label: "Flashcards",
    description: "Flip cards for rapid revision.",
    icon: Layers,
    iconColor: "text-amber-600 dark:text-amber-400",
    iconBg: "bg-amber-500/10",
  },
  {
    id: "mind-map",
    label: "Mind map",
    description: "See how the concepts connect at a glance.",
    icon: Network,
    iconColor: "text-teal-600 dark:text-teal-400",
    iconBg: "bg-teal-500/10",
  },
  {
    id: "quiz",
    label: "Quiz",
    description: "Auto-graded MCQs with explanations.",
    icon: HelpCircle,
    iconColor: "text-purple-600 dark:text-purple-400",
    iconBg: "bg-purple-500/10",
  },
  {
    id: "slides",
    label: "Slides",
    description: "Skim the chapter in five minutes.",
    icon: Presentation,
    iconColor: "text-indigo-600 dark:text-indigo-400",
    iconBg: "bg-indigo-500/10",
  },
  {
    id: "notes",
    label: "Concept notes",
    description: "Tight, exam-focused revision notes.",
    icon: FileText,
    iconColor: "text-emerald-600 dark:text-emerald-400",
    iconBg: "bg-emerald-500/10",
  },
  {
    id: "question-bank",
    label: "Question bank",
    description: "Practice questions and PYQs.",
    icon: CheckCircle2,
    iconColor: "text-orange-600 dark:text-orange-400",
    iconBg: "bg-orange-500/10",
  },
];

/**
 * Canonical study-mode ids as the UI spells them (hyphenated). The backend's
 * VALID_MODES uses underscores; `normalizeModeId` is the bridge.
 */
export type StudyModeId =
  | "textbook" | "notes" | "question-bank" | "slides"
  | "podcast"  | "video"
  | "flashcards" | "mind-map" | "quiz" | "ai-tutor";

/**
 * Convert a UI mode id into the id the backend understands.
 *
 * The two vocabularies drifted: the UI says "question-bank" / "mind-map", the
 * progress handler's VALID_MODES says "question_bank" / "mindmap". Sending the
 * hyphenated form made `mode_started` 400 for those two viewers, so they never
 * entered `modes_used` at all. Same mapping `useSignedUrl` needs for asset types,
 * which is why it lives here rather than being spelled out twice.
 */
export function normalizeModeId(modeId: string): string {
  const m = modeId.trim().toLowerCase().replace(/-/g, "_");
  return m === "mind_map" ? "mindmap" : m;
}

/** Map study-mode id -> route segment, kept in sync with src/app/routes.tsx */
export function routeForMode(modeId: string): string {
  if (modeId === "ai-tutor") return "?tutor=open";
  return `/${modeId}`;
}
